import React from "react";
import {
  faCircleInfo,
  faXmark,
  faFolderOpen,
  faListCheck,
  faClock,
  faFileContract,
  faCar,
  faBoxes,
  faShieldAlt,
  faMoneyBill,
  faFileInvoiceDollar,
  faUserShield,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const generalTaskCategories = [
  {
    icon: faFileContract,
    title: "CONTRACT",
    description: "Tasks related to vendor contracts, renewals, or agreement management.",
  },
  {
    icon: faShieldAlt,
    title: "INSURANCE",
    description: "Tasks for policy renewal, claim tracking, or insurance expiry reminders.",
  },
  {
    icon: faBoxes,
    title: "INVENTORY",
    description: "Stock monitoring, reorder levels, or asset tagging and audits.",
  },
  {
    icon: faCar,
    title: "VEHICLE",
    description: "Maintenance, registration renewals, or pollution checks for vehicles.",
  },
  {
    icon: faFileInvoiceDollar,
    title: "GST",
    description: "GST filings, due dates, compliance checks, and notice responses.",
  },
  {
    icon: faMoneyBill,
    title: "PAYROLL",
    description: "Salary processing, PF/ESI, bonus adjustments, and compliance validation.",
  },
  {
    icon: faFolderOpen,
    title: "DOCUMENT",
    description: "Tasks that relate to document collection, sharing, verification, or approval.",
  },
  {
    icon: faTasks,
    title: "GENERAL",
    description: "Ad-hoc or one-off operational tasks that don’t fit other categories.",
  },
];

const taskRoles = [
  { title: "ASSIGNEE", description: "Responsible for completing the task." },
  { title: "REVIEWER", description: "Reviews and verifies the work post completion." },
  { title: "APPROVER", description: "Has the final authority to approve or reject the task." },
];

const priorities = [
  { title: "HIGH", description: "Requires urgent attention." },
  { title: "MEDIUM", description: "Important but not time-critical." },
  { title: "LOW", description: "Can be handled at leisure or during idle time." },
];

const statuses = [
  { title: "PENDING", description: "Task is created but not yet started." },
  { title: "IN_PROGRESS", description: "Task work is currently ongoing." },
  { title: "COMPLETED", description: "Task work has been finished." },
  { title: "REJECTED", description: "Task was declined or closed without execution." },
];

const GeneralTaskHelp: React.FC<Props> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleInfo} /> General Task Module Guide
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-red-500">
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Understand the categories, lifecycle, and required fields used in managing general tasks.
        </p>

        {/* Categories Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-indigo-700 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faFolderOpen} />
            Task Categories
          </h3>
          <ul className="space-y-3 pl-5 border-l-4 border-indigo-300">
            {generalTaskCategories.map((cat) => (
              <li key={cat.title} className="text-sm text-gray-700 flex items-start gap-2">
                <FontAwesomeIcon icon={cat.icon} className="pt-1 text-indigo-500" />
                <div>
                  <strong>{cat.title}</strong>: {cat.description}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Priority Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-indigo-700 flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faClock} />
            Priority
          </h3>
          <ul className="list-disc ml-6 text-sm text-gray-700 space-y-2">
            {priorities.map((p) => (
              <li key={p.title}>
                <strong>{p.title}</strong>: {p.description}
              </li>
            ))}
          </ul>
        </div>

        {/* Status Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-indigo-700 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faListCheck} />
            Status
          </h3>
          <ul className="list-disc ml-6 text-sm text-gray-700 space-y-2">
            {statuses.map((s) => (
              <li key={s.title}>
                <strong>{s.title}</strong>: {s.description}
              </li>
            ))}
          </ul>
        </div>

        {/* Roles */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-indigo-700 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faUserShield} />
            Assignment Roles
          </h3>
          <ul className="list-disc ml-6 text-sm text-gray-700 space-y-2">
            {taskRoles.map((r) => (
              <li key={r.title}>
                <strong>{r.title}</strong>: {r.description}
              </li>
            ))}
          </ul>
        </div>

        {/* Usage Steps */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-indigo-800 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faTasks} />
            How to Use
          </h3>
          <ol className="list-decimal ml-6 text-sm text-gray-700 space-y-2">
            <li>Click on <strong>"Create General Task"</strong> from the task menu or dashboard.</li>
            <li>Select a <strong>Category</strong> such as Vehicle, Contract, or Insurance.</li>
            <li>Enter relevant details: <strong>Title, Description, Status, and Priority</strong>.</li>
            <li>Assign users with roles such as <strong>Assignee, Reviewer</strong> or <strong>Approver</strong>.</li>
            <li>Link to a related module using <code>entity_type</code> and <code>entity_id</code> if applicable.</li>
          </ol>
        </div>

        {/* Footer Note */}
        <div className="mt-6 border-t pt-4 text-xs text-gray-500">
          <p>
            <strong>Note:</strong> General tasks are ideal for recurring, operational, or tracking activities
            outside of project modules.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GeneralTaskHelp;